import type { BasNavigationStateRecord } from "../../types";
import { matchSegment } from "./pathMatching";

/**
 * Result of traversing the navigation state tree
 */
type TraversalResult = {
  record: BasNavigationStateRecord;
  slug: string;
  isDynamic: boolean;
  dynamicPattern?: RegExp;
};

/**
 * Shared utility for traversing the navigation state tree and finding matching records.
 * This function is used by both getPathFromNavigationState and getParsedNavigationContextFromNavigationState.
 *
 * @param records - The current level of records to search through
 * @param key - The key to match against (can be actual value or placeholder)
 * @returns The matching record and metadata, or null if no match found
 */
export const findMatchingNavigationStateRecord = (
  records: BasNavigationStateRecord[],
  key: string,
): TraversalResult | null => {
  if (!records || !key) {
    return null;
  }

  // First try to find an exact match
  const exactMatch = records.find((record) => record.id === key);
  if (exactMatch) {
    return {
      record: exactMatch,
      slug: exactMatch.slug || exactMatch.id,
      isDynamic: false,
    };
  }

  // If no exact match found, look for a dynamic placeholder
  const dynamicMatch = records.find(
    (record) => record.id.startsWith("[") && record.id.endsWith("]"),
  );

  if (dynamicMatch) {
    const slug = dynamicMatch.slug || dynamicMatch.id;

    // Check if this is a partial dynamic slug using our shared utility
    const matchResult = matchSegment("dummy", slug);

    if (
      matchResult.isDynamic &&
      slug.includes("[") &&
      slug.includes("]") &&
      !slug.match(/^\[.*\]$/)
    ) {
      // Create a regex pattern for the partial dynamic slug
      const pattern = slug.replace(/\[([^\]]+)\]/g, "(.+)");
      const dynamicPattern = new RegExp(`^${pattern}$`);

      return {
        record: dynamicMatch,
        slug,
        isDynamic: true,
        dynamicPattern,
      };
    }

    return {
      record: dynamicMatch,
      slug,
      isDynamic: true,
    };
  }

  return null;
};

/**
 * Recursively traverses the navigation state tree following a path of keys
 *
 * @param records - The current level of records
 * @param keys - The remaining keys to process
 * @param callback - Function called for each matched record
 * @returns The result of the traversal or null
 */
export const traverseNavigationState = <T>(
  records: BasNavigationStateRecord[],
  keys: string[],
  callback: (params: {
    record: BasNavigationStateRecord;
    key: string;
    remainingKeys: string[];
    traversalResult: TraversalResult;
    depth: number;
  }) => { continue: boolean; result?: T },
  depth: number = 0,
): T | null => {
  if (!keys.length || !records) {
    return null;
  }

  const currentKey = keys[0];
  const remainingKeys = keys.slice(1);

  const traversalResult = findMatchingNavigationStateRecord(
    records,
    currentKey,
  );
  if (!traversalResult) {
    return null;
  }

  const callbackResult = callback({
    record: traversalResult.record,
    key: currentKey,
    remainingKeys,
    traversalResult,
    depth,
  });

  if (!callbackResult.continue || callbackResult.result !== undefined) {
    return callbackResult.result ?? null;
  }

  // Continue traversing if there are more keys and the record has steps
  if (remainingKeys.length > 0 && traversalResult.record.steps?.length) {
    return traverseNavigationState(
      traversalResult.record.steps as BasNavigationStateRecord[],
      remainingKeys,
      callback,
      depth + 1,
    );
  }

  // No more keys to process or no more steps
  return null;
};
